<template lang="pug">
yz-dialog(v-model="showDialog" fullscreen persistent)
  yz-card.d-flex.flex-column(style="height: 100%;")
    //- Header
    yz-dialog-fullscreen-header(:title="headerTitle" :loading="isSubmitting" @back="onBack")
      template(#actions)
        .d-flex.align-center(style="gap: 12px")
          yz-btn(size="lg" text label="Cancel" :disabled="isSubmitting" @click="onCancel")
          yz-btn(size="lg" :label="headerTitle" :loading="isSubmitting" @click="onSubmit")

    //- Content: Two-column layout
    yz-dialog-fullscreen-content.flex-grow-1.overflow-y-auto
      v-row(no-gutters)
        //- Left: Adjusted invoices table
        v-col(cols="12" md="9")
          .pr-md-6
            .yz-heading-9.neutral700--text Adjusted invoices
            .yz-regular-body-xs.neutral700--text.mb-4 Below are the invoices affected after the consolidated e-Invoice was submitted.

            yz-card.pa-5
              yz-data-table(:headers="headers" :items="localInvoices" item-key="pid" :loading="isLoading" :items-per-page="pageSize" hide-default-footer)
                template(v-slot:no-data)
                  .py-8
                    .text-sm.neutral500--text.text-center No invoices available

                //- Date column
                template(v-slot:item.date="{ item }")
                  div.yz-regular-body-sm.neutral700--text {{ item.date | humanizeDate }}

                //- Invoice number column
                template(v-slot:item.invoice_no="{ item }")
                  div.yz-regular-body-sm.neutral700--text {{ `#${item.invoice_no}` }}

                //- Payment amount column with decorative info icon
                template(v-slot:item.amount="{ item }")
                  .d-flex.align-center
                    div.yz-regular-body-sm.neutral700--text {{ item.amount | displayPrice($currency) }}
                    v-icon.ml-1(size="16" color="neutral400") $yzi-info-circle

                //- Changed amount column
                template(v-slot:item.changed_amount="{ item }")
                  div.yz-medium-body-sm.neutral700--text {{ item.changed_amount | displayPrice($currency) }}

                //- Actions column
                template(v-slot:item.actions="{ item }")
                  yz-btn(outlined size="sm" icon="$yzi-arrow-narrow-up-right" @click.stop="onOpenInvoice(item)")

              //- Footer: Total + Pagination
              .d-flex.align-center.mt-4
                .text-sm.neutral600--text Total:
                  span.font-semibold  {{ totalOriginalAmount | displayPrice($currency) }}
                  |  ({{ localInvoices.length }} {{ localInvoices.length === 1 ? 'invoice' : 'invoices' }})
                v-spacer
                yz-pagination(v-if="localInvoices.length > pageSize" v-model="currentPage" :page-size="pageSize" :count="localInvoices.length" type="page" arrow)

        v-divider.yz-neutral-300(vertical)
        //- Right: Note details sidebar
        v-col(cols="12" md="3")
          div.pa-5
            .yz-heading-9.neutral700--text {{ sidebarTitle }}
            .yz-regular-body-xs.neutral700--text.mb-4 {{ sidebarDescription }}

            //- Total card
            .mb-4.pa-4.rounded-lg.bg-neutral-300
              .yz-medium-body-sm.neutral700--text Total
              .yz-heading-5.neutral700--text {{ totalChangedAmount | displayPrice($currency) }}

            //- Submitted date
            div.mb-4
              .yz-medium-body-sm.neutral700--text.mb-1 Submitted date
              yz-menu(offset-y min-width="auto")
                template(v-slot:activator="{ on, attrs }")
                  yz-text-field(v-model="formattedSubmittedDate" placeholder="Pick a date" readonly clickable v-bind="attrs" v-on="on")
                    template(#append)
                      v-icon(color="neutral500" size="18") $yzi-calendar
                yz-date-picker(v-model="submittedDate" scrollable)
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'CreditDebitNoteDialog',
  props: {
    open: {
      type: Boolean,
      default: false
    },
    einvoiceId: {
      type: [Number, String],
      default: null
    },
    noteType: {
      type: String,
      default: 'debit',
      validator: (value) => ['credit', 'debit'].includes(value)
    }
  },
  data: () => ({
    isLoading: false,
    isSubmitting: false,
    localInvoices: [],
    submittedDate: new Date().toISOString().split('T')[0],
    currentPage: 1,
    pageSize: 10
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    showDialog: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
    headerTitle() {
      return this.noteType === 'credit' ? 'Create credit note' : 'Create debit note'
    },
    sidebarTitle() {
      return this.noteType === 'credit' ? 'Credit note details' : 'Debit note details'
    },
    sidebarDescription() {
      return this.noteType === 'credit'
        ? 'Key information related to the submitted credit note.'
        : 'Key information related to the submitted debit note.'
    },
    headers() {
      return [
        { text: 'DATE', value: 'date', class: 'text-table-header' },
        { text: 'INVOICE NO.', value: 'invoice_no', class: 'text-table-header' },
        { text: 'PAYMENT AMOUNT', value: 'amount', class: 'text-table-header' },
        { text: 'CHANGED AMOUNT', value: 'changed_amount', class: 'text-table-header' },
        { text: '', value: 'actions', class: 'text-table-header', sortable: false, width: '52' },
      ]
    },
    totalOriginalAmount() {
      return this.localInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0)
    },
    totalChangedAmount() {
      return this.localInvoices.reduce((sum, inv) => sum + Math.abs(inv.changed_amount || 0), 0)
    },
    formattedSubmittedDate() {
      if (!this.submittedDate) return ''
      return this.$options.filters.humanizeDate(this.submittedDate)
    }
  },
  mounted() {
    this.fetchInvoices()
  },
  methods: {
    ...mapActions(['showMessage']),

    fetchInvoices() {
      if (!this.einvoiceId) return
      this.isLoading = true

      this.$axios.get(`/einvoices/${this.einvoiceId}/adjustment_invoices/`)
        .then(resp => {
          const results = resp.data || []
          this.localInvoices = results.map(item => ({
            pid: item.invoice_id,
            invoice_no: item.invoice_no,
            date: item.date,
            amount: item.payment_amount || 0,
            changed_amount: item.changed_amount || 0
          }))
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          this.showDialog = false
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    formatChangedAmount(amount) {
      if (!amount) return 'RM 0.00'
      const absAmount = this.$options.filters.displayPrice(Math.abs(amount))
      if (amount > 0) return `+RM ${absAmount}`
      if (amount < 0) return `-RM ${absAmount}`
      return `RM ${absAmount}`
    },

    onBack() {
      this.showDialog = false
      this.$emit('close')
    },

    onCancel() {
      this.showDialog = false
      this.$emit('cancel')
    },

    onSubmit() {
      if (!this.einvoiceId) return
      this.isSubmitting = true

      this.$axios.post(`/einvoices/${this.einvoiceId}/adjustments/`, { submitted_date: this.submittedDate })
        .then(() => {
          this.showMessage(['success', `${this.noteType === 'credit' ? 'Credit' : 'Debit'} note submitted successfully`])
          this.$emit('submit')
          this.showDialog = false
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isSubmitting = false
        })
    },

    onOpenInvoice(item) {
      window.open(`/admin/clinic/checkout/${item.pid}`, '_blank')
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .yz-card-text {
  height: 100%;
}
</style>
